import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TbBath, TbSofa } from 'react-icons/tb';
import { generatePath, useNavigate } from 'react-router-dom';

import { RoomType, SpaceReadModel } from '@client/api';
import {
  getBuildingId,
  isDefinedNumber,
  isDefinedString,
  Skeleton,
  TableCell,
  TableRow,
  Text,
  toNumberString,
  toPriceWithIntervalString,
} from '@client/toolkit';

import { PortfolioPaths } from '../portfolio.router';
import { useBathroomsCount } from '../spaces/hooks/use-bathrooms-count';
import { useRoomsCount } from '../spaces/hooks/use-rooms-count';

interface SpaceRowProps {
  space?: SpaceReadModel;
  replaceHistory?: boolean;
}

export function SpaceRow({ space, replaceHistory = false }: SpaceRowProps) {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!space || !space.id || !space.discriminator) return;

    const buildingId = getBuildingId(space.path);

    navigate(
      generatePath(PortfolioPaths.Space, {
        buildingId: buildingId,
        spaceId: space.id,
        discriminator: space.discriminator,
      }),
      {
        replace: replaceHistory,
      },
    );
  }, [space, navigate, replaceHistory]);

  const roomsCount = useRoomsCount(space);
  const bathroomsCount = useBathroomsCount(space);

  return (
    <>
      {!space && (
        <TableRow>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" />
          </TableCell>
          {/* <TableCell>
            <Skeleton variant="text" />
          </TableCell> */}
        </TableRow>
      )}

      {!!space && (
        <TableRow hover onClick={handleClick}>
          <TableCell className="font-semibold">
            {space.name}
            {space.discriminator === 'ResidentialUnit' && (
              <div className="flex items-center divide-x text-xs mt-1">
                <div className="flex items-center space-x-2 pr-2" title={tGlobal('entity.room', { context: 'plural' })}>
                  <TbSofa className="text-secondary" />
                  <Text className="font-medium leading-none" emptyValue={0} isEmpty={!isDefinedNumber(roomsCount)}>
                    {roomsCount}
                  </Text>
                </div>

                <div
                  className="flex items-center space-x-2 pl-2"
                  title={tType(`RoomType.${'Bathroom' as RoomType}`, { context: 'plural' })}
                >
                  <TbBath className="text-secondary" />
                  <Text className="font-medium leading-none" emptyValue={0} isEmpty={!isDefinedNumber(bathroomsCount)}>
                    {bathroomsCount}
                  </Text>
                </div>
              </div>
            )}
          </TableCell>
          <TableCell>
            <Text as="span" isEmpty={!isDefinedString(space.discriminator)}>
              {tType(`SpaceDiscriminator.${space.discriminator}`)}
            </Text>
          </TableCell>

          <TableCell>
            <Text as="span" isEmpty={!space.assetClass}>
              {space.assetClass ? tType(`AssetClass.${space.assetClass}`) : '-'}
            </Text>
          </TableCell>

          <TableCell>
            {toNumberString(space?.area?.metric ?? undefined, undefined, {
              unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
            })}
          </TableCell>

          <TableCell>{space.tenantName ?? '-'}</TableCell>
          <TableCell align="left">
            <span style={space?.status === 'Rented' ? { color: '#10b981' } : undefined}>
              {space?.status ? tType(`VacancyStatus.${space?.status}`) : '-'}
            </span>
          </TableCell>
          {/* <TableCell>{space.rent ? toPriceWithIntervalString(space.rent) : '-'}</TableCell> */}
        </TableRow>
      )}
    </>
  );
}
